import { Phone, MapPin, Clock, Mail, MessageCircle, CheckCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import './Contact.css';

const Contact = () => {
  const whatsappUrl = 'https://wa.me/919886332528';

  const steps = [
    {
      number: '1',
      icon: <MessageCircle size={32} />,
      title: 'Call or WhatsApp us',
      description: 'Reach out via phone or WhatsApp at your convenience'
    },
    {
      number: '2',
      icon: <CheckCircle size={32} />,
      title: 'Share your condition and preferred time',
      description: 'Tell us about your condition and when you\'d like to visit'
    },
    {
      number: '3',
      icon: <Phone size={32} />,
      title: 'Confirm your appointment',
      description: 'We\'ll confirm your appointment and send you all details'
    }
  ];

  return (
    <div className="contact-page">
      <div className="page-hero">
        <div className="container">
          <h1>Book Your Physiotherapy Appointment in Panjim</h1>
          <p className="page-subtitle">
            Get in touch with Panjim's most trusted physiotherapy clinic
          </p>
        </div>
      </div>

      {/* Contact Details & Map */}
      <section className="section contact-main">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info-section">
              <h2>Get In Touch</h2>
              <div className="contact-details-list">
                <div className="detail-item">
                  <div className="contact-icon">
                    <Phone size={24} />
                  </div>
                  <div className="contact-detail-text">
                    <h3>Phone Number</h3>
                    <p>
                      <a href="tel:9886332528">9886332528</a>
                    </p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <MessageCircle size={24} />
                  </div>
                  <div className="contact-detail-text">
                    <h3>WhatsApp</h3>
                    <p>
                      <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                        Chat with us on WhatsApp
                      </a>
                    </p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <MapPin size={24} />
                  </div>
                  <div className="contact-detail-text">
                    <h3>Address</h3>
                    <p>
                      House no, H-4, Miramar,<br />
                      Panaji, Goa 403001
                    </p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <Clock size={24} />
                  </div>
                  <div className="contact-detail-text">
                    <h3>Working Hours</h3>
                    <p>
                      Monday – Saturday: 9:00 AM – 8:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <Mail size={24} />
                  </div>
                  <div className="contact-detail-text">
                    <h3>Email</h3>
                    <p>
                      <a href="mailto:info@drmanazphysio.com">info@drmanazphysio.com</a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="map-section">
              <div className="map-container-contact">
                <iframe
                  title="Dr. Manaz Physiotherapy Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3844.5!2d73.8!3d15.5!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTXCsDMwJzAwLjAiTiA3M8KwNDgnMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Book */}
      <section className="section booking-steps">
        <div className="container">
          <div className="section-header">
            <h2>How to Book Your Appointment</h2>
            <p>Simple 3-step process</p>
          </div>
          <div className="steps-grid-contact">
            {steps.map((step, index) => (
              <div key={index} className="step-card-contact">
                <div className="step-number-contact">{step.number}</div>
                <div className="step-icon-contact">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section faq-section-contact">
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="faq-container">
            <Accordion type="single" collapsible className="faq-accordion">
              <AccordionItem value="item-1">
                <AccordionTrigger>Do I need a doctor's referral?</AccordionTrigger>
                <AccordionContent>
                  No, you don't need a doctor's referral to visit our clinic. However, if you have one, please bring it along as it helps us understand your condition better. You can directly book an appointment with us for any physiotherapy needs.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2">
                <AccordionTrigger>What should I bring to my first session?</AccordionTrigger>
                <AccordionContent>
                  Please bring any relevant medical reports, X-rays, MRI scans, or doctor's prescriptions. Wear comfortable, loose-fitting clothes that allow easy movement. If you're coming for a specific condition, bring any previous physiotherapy records if available.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3">
                <AccordionTrigger>Do you accept insurance or cashless payment?</AccordionTrigger>
                <AccordionContent>
                  We accept cash and digital payments (UPI, cards). For insurance claims, we provide detailed invoices and receipts that you can submit to your insurance company for reimbursement. Contact us for specific insurance-related queries.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4">
                <AccordionTrigger>How long is a standard session?</AccordionTrigger>
                <AccordionContent>
                  A typical physiotherapy session lasts 30-45 minutes, depending on your condition and the treatment required. Your first consultation may take slightly longer (45-60 minutes) as we need to conduct a thorough assessment.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5">
                <AccordionTrigger>Do you provide home visits on weekends?</AccordionTrigger>
                <AccordionContent>
                  Yes, we provide home visit services on Saturdays. For Sunday appointments, please contact us in advance as availability may be limited. Weekend slots fill up quickly, so we recommend booking early.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-dark cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Start Your Recovery Journey?</h2>
            <p>Book your appointment today and take the first step towards a pain-free life</p>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn-primary large">
              <MessageCircle size={20} />
              Book Appointment on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
